/**
 * Platform browser-service base URL resolver
 *
 * The browser-service (Xvfb + Playwright + noVNC + WS frame stream) is a
 * platform-shared service running on prest0-vm, NOT a per-account VM workload.
 * It does NOT belong under `accounts/{accountId}/vm/main.baseUrl` — that field
 * points at each tenant's Preston brain VM (e.g. Fernando VM), which does not
 * host the browser-service publicly.
 *
 * Single source of truth: Firestore document `system/prest0Core/services/main`,
 * field `browserServiceUrl`. Production should set this to e.g.
 *   `https://34-60-222-164.sslip.io/browser`
 *
 * The platform-shared BEARER lives on a sibling doc,
 * `system/prest0Core/services/internalAuth` (BROWSER_SERVICE_INTERNAL_AUTH_DOC),
 * which firestore.rules opens only to verified members of the internal org —
 * see the token section below (security remediation slice 3, 2026-09-15).
 *
 * Resolution order:
 *   1. `window.PREST0_BROWSER_SERVICE_BASE_URL` runtime override (staging / debug)
 *   2. Firestore `system/prest0Core/services/main.browserServiceUrl`
 *   3. Production compile-time default (current prest0-vm sslip hostname)
 *   4. localhost fallback for local dev
 *
 * The resolver exposes a sync getter (returns the most recently known value,
 * starting from the compile-time default) and an async `loadBrowserServiceBaseUrl`
 * that hydrates the cache from Firestore. Hydration is fire-and-forget at module
 * load and idempotent; callers that need the freshest value should `await`
 * `getBrowserServiceBaseUrl()` before making a request.
 *
 * The compile-time default deliberately points at the current prest0-vm public
 * sslip hostname so a Firestore outage or unconfigured field does not bring
 * /home or /agent-activity down.
 */
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { ACCOUNT_FIRESTORE_PREFIX } from '../prest0nRuntime/constants';
import { PREST0_INC_ACCOUNT_ID } from '../../config/internalAccess';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compile-time production default. Points at prest0-vm via dashed-sslip.
 * Caddy on prest0-vm must serve `/browser/*` at this hostname (see deployment
 * runbook). Update here if the prest0-vm public hostname ever moves.
 */
export const PRODUCTION_DEFAULT_BROWSER_SERVICE_URL =
  'https://34-60-222-164.sslip.io/browser';

/** Localhost dev default — matches browser-service's local listen port. */
export const LOCALHOST_BROWSER_SERVICE_URL = 'http://localhost:3040';

/**
 * Dead / stale sslip hostnames that historically appeared in hardcoded
 * constants in old bundles. If a Firestore-or-window override accidentally
 * points at one of these, rewrite to the production default so the page does
 * not silently break for users on slow CDNs.
 *
 * The 2026-05-13 entries cover the day Fernando's tenant VM rolled over from
 * 34.121.30.211 → 35.238.149.88. browser-service is platform-shared (lives on
 * prest0-vm, not the Fernando tenant), so any baseUrl pointing at a Fernando-
 * tenant hostname is by definition stale and must be rewritten to the prest0-vm
 * default rather than left to fail.
 */
const STALE_BROWSER_SERVICE_HOSTS: readonly string[] = [
  '34-46-30-95.sslip.io',
  '34.46.30.95',
  '34-56-64-149.sslip.io',
  '34.56.64.149',
  '136-111-40-165.sslip.io',
  '136.111.40.165',
  '34-121-30-211.sslip.io', // 2026-05-13: pre-rollover Fernando tenant
  '34.121.30.211',
  '34-30-187-79.sslip.io', // pre-2026-04 UID-keyed VM
  '34.30.187.79',
];

const FIRESTORE_DOC_SEGMENTS = [
  'system',
  'prest0Core',
  'services',
  'main',
] as const;

/** Firestore field name on `system/prest0Core/services/main`. */
export const BROWSER_SERVICE_URL_FIELD = 'browserServiceUrl';

/**
 * Firestore document carrying the platform-shared browser-service BEARER
 * (security remediation slice 3, 2026-09-15). It rode the URL doc above until
 * then — a doc every real signed-in user reads — and now sits on its own doc
 * that firestore.rules opens only to verified members of the internal org, the
 * only people the shared host serves (accountVmBase.ts sends every other org
 * to its own workstation, whose token rides accounts/{id}/vm/main). The same
 * literal is spelled by the rules, the installer that publishes it and the
 * server-side resolver; scripts/check-internal-auth-doc-parity.mjs fails the
 * build if any of them drift.
 */
export const BROWSER_SERVICE_INTERNAL_AUTH_DOC = 'system/prest0Core/services/internalAuth';

/**
 * Firestore field name for the platform-shared browser-service bearer token
 * (2026-08-11, P0 security item #6), on BROWSER_SERVICE_INTERNAL_AUTH_DOC
 * since 2026-09-15 (before that on the URL doc, readable by every signed-in
 * principal). A reader the rules deny gets permission-denied, which this
 * module treats as "no token" — see getBrowserServiceAuthToken.
 *
 * The service now supports a bearer-token gate on all `/api/*` routes and the
 * `/ws/:sessionId` viewer (server env `BROWSER_SERVICE_AUTH_TOKEN`, loaded
 * from /etc/prest0n/account.env). This field is the web client's copy of that
 * token. Deploy-safe rollout contract:
 *   - field absent / token env unset  → client sends no auth, server allows
 *     all (today's behavior — nothing breaks before ops provisions it);
 *   - both provisioned               → client attaches
 *     `Authorization: Bearer <token>` on fetches and `?auth=<token>` on the
 *     viewer WebSocket URL.
 */
export const BROWSER_SERVICE_TOKEN_FIELD = 'browserServiceToken';

// ─────────────────────────────────────────────────────────────────────────────
// Internal cache
// ─────────────────────────────────────────────────────────────────────────────

function isLocalhost(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.location.hostname === 'localhost';
  } catch {
    return false;
  }
}

function readWindowOverride(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const override = (window as unknown as {
      PREST0_BROWSER_SERVICE_BASE_URL?: string;
    }).PREST0_BROWSER_SERVICE_BASE_URL;
    if (typeof override === 'string' && override.trim()) {
      return override.trim();
    }
  } catch {
    /* SSR / non-browser — ignore */
  }
  return null;
}

/**
 * Debug override for the auth token (2026-08-11), mirroring the base-URL
 * override: `window.PREST0_BROWSER_SERVICE_AUTH_TOKEN`. Lets someone pointing
 * a local page at prod (via the URL override, which short-circuits Firestore)
 * still supply the token without a reload.
 */
function readWindowTokenOverride(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const override = (window as unknown as {
      PREST0_BROWSER_SERVICE_AUTH_TOKEN?: string;
    }).PREST0_BROWSER_SERVICE_AUTH_TOKEN;
    if (typeof override === 'string' && override.trim()) {
      return override.trim();
    }
  } catch {
    /* SSR / non-browser — ignore */
  }
  return null;
}

function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/**
 * If the resolved URL points at a known-dead sslip host, rewrite it to the
 * production default. This is a near-term safety net so a stale Firestore
 * value (or a stale cached `window.PREST0_BROWSER_SERVICE_BASE_URL`) does not
 * silently break /home.
 */
function rewriteIfStale(url: string): string {
  for (const dead of STALE_BROWSER_SERVICE_HOSTS) {
    if (url.includes(dead)) {
      return PRODUCTION_DEFAULT_BROWSER_SERVICE_URL;
    }
  }
  return url;
}

function compileTimeDefault(): string {
  if (isLocalhost()) return LOCALHOST_BROWSER_SERVICE_URL;
  return PRODUCTION_DEFAULT_BROWSER_SERVICE_URL;
}

let cachedBaseUrl: string = (() => {
  // Initial: window override wins, otherwise compile-time default.
  const override = readWindowOverride();
  if (override) return trimTrailingSlash(rewriteIfStale(override));
  return trimTrailingSlash(compileTimeDefault());
})();

let inflightLoad: Promise<string> | null = null;
let loaded = false;

// ── The shared host as the PLATFORM names it (S1b-3a, 2026-09-26) ──────────
//
// `cachedBaseUrl` is the EFFECTIVE base, and the window override writes it.
// That override is set by /desktop's `?svc=` (and by hand in devtools), so it
// cannot also answer "is this host the platform-shared one, which the shared
// internal key and a Prest0 Inc ticket authenticate against?". Until
// 2026-09-26 it did: isSharedServiceBase compared a host with the effective
// base, so a crafted `/desktop?svc=https://<any-ip>.sslip.io/browser` made the
// page attach the internal key, or the person's Prest0 Inc ticket, to every
// call the chat then made to that address. That question is answered here, by
// the Firestore value or the compile-time default, never by the override.
let canonicalSharedBase: string = trimTrailingSlash(compileTimeDefault());
let canonicalLoaded = false;
let inflightCanonicalLoad: Promise<string> | null = null;

/** Read `system/prest0Core/services/main.browserServiceUrl`, else the compile-time default. */
async function readCanonicalSharedBase(): Promise<string> {
  if (isLocalhost()) return LOCALHOST_BROWSER_SERVICE_URL;
  const db = getFirestore(getApp());
  const snap = await getDoc(
    doc(db, FIRESTORE_DOC_SEGMENTS[0], FIRESTORE_DOC_SEGMENTS[1], FIRESTORE_DOC_SEGMENTS[2], FIRESTORE_DOC_SEGMENTS[3])
  );
  const raw = snap.exists() ? snap.data()?.[BROWSER_SERVICE_URL_FIELD] : undefined;
  return typeof raw === 'string' && raw.trim()
    ? trimTrailingSlash(rewriteIfStale(raw.trim()))
    : trimTrailingSlash(compileTimeDefault());
}

// Auth-token cache (2026-08-11). Separate module state from the URL cache:
// since slice 3 (2026-09-15) the token lives on its own members-only doc, so
// the URL load never sees it and it resolves on its own path, on first use.
let cachedAuthToken: string | null = null;
let tokenLoaded = false;
let inflightTokenLoad: Promise<string | null> | null = null;
/** Warn once on token-load failure so unit tests / offline dev stay quiet. */
let tokenLoadWarned = false;
/**
 * A DENIED token read is the rules saying "not an internal member" — an
 * answer, not a failure. It is held for this long before one re-ask, so a
 * page never hammers Firestore with reads it knows are denied, while a user
 * who becomes a verified member in the same tab is not stuck until reload.
 */
const TOKEN_DENIED_HOLD_MS = 5 * 60 * 1000;
let tokenDeniedUntilMs = 0;

function normalizeTokenField(raw: unknown): string | null {
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** The Firestore SDK's permission-denied error, in either of its spellings. */
function isPermissionDenied(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  return code === 'permission-denied' || code === 'PERMISSION_DENIED';
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the most recently known browser-service base URL synchronously.
 * Initially the compile-time production default (or localhost in dev). After
 * the first successful `loadBrowserServiceBaseUrl` call, this returns the
 * Firestore-resolved value.
 */
export function getCachedBrowserServiceBaseUrl(): string {
  // window override is volatile — always re-read so callers can flip it for
  // debugging without a reload after the first call.
  const override = readWindowOverride();
  if (override) return trimTrailingSlash(rewriteIfStale(override));
  return cachedBaseUrl;
}

/**
 * Fetches the canonical browser-service base URL from Firestore and updates
 * the in-memory cache. Resolves with the resolved URL. Subsequent calls reuse
 * the cached value (and the cached Firestore read) for the lifetime of the
 * page — force a refresh by setting `force=true`.
 */
export async function loadBrowserServiceBaseUrl(
  opts: { force?: boolean } = {}
): Promise<string> {
  if (!opts.force) {
    // Window override always wins; short-circuit Firestore.
    const override = readWindowOverride();
    if (override) {
      cachedBaseUrl = trimTrailingSlash(rewriteIfStale(override));
      // The override answers "where do I send this", never "which host is the
      // shared one" — that still comes from Firestore (S1b-3a).
      void loadCanonicalSharedBrowserServiceBase().catch(() => undefined);
      return cachedBaseUrl;
    }
    if (loaded) return cachedBaseUrl;
    if (inflightLoad) return inflightLoad;
  }

  inflightLoad = (async () => {
    try {
      // On localhost we never hit Firestore for this — keep dev hermetic.
      if (isLocalhost()) {
        cachedBaseUrl = LOCALHOST_BROWSER_SERVICE_URL;
        loaded = true;
        return cachedBaseUrl;
      }

      const db = getFirestore(getApp());
      const ref = doc(
        db,
        FIRESTORE_DOC_SEGMENTS[0],
        FIRESTORE_DOC_SEGMENTS[1],
        FIRESTORE_DOC_SEGMENTS[2],
        FIRESTORE_DOC_SEGMENTS[3]
      );
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        // The bearer no longer rides this doc (slice 3, 2026-09-15) — the
        // token resolves on its own path, from its own members-only doc.
        const raw = data?.[BROWSER_SERVICE_URL_FIELD];
        if (typeof raw === 'string' && raw.trim()) {
          cachedBaseUrl = trimTrailingSlash(rewriteIfStale(raw.trim()));
          loaded = true;
          // The same read names the shared host (S1b-3a).
          canonicalSharedBase = cachedBaseUrl;
          canonicalLoaded = true;
          return cachedBaseUrl;
        }
      }
      // Field unset → fall back to compile-time default. Keep `loaded=true` so
      // we don't pummel Firestore on every call.
      cachedBaseUrl = trimTrailingSlash(compileTimeDefault());
      loaded = true;
      canonicalSharedBase = cachedBaseUrl;
      canonicalLoaded = true;
      return cachedBaseUrl;
    } catch (err) {
      console.warn(
        '[browserService/baseUrl] Failed to load Firestore config, using compile-time default',
        err
      );
      cachedBaseUrl = trimTrailingSlash(compileTimeDefault());
      // Do NOT set loaded=true on error — let the next caller retry.
      return cachedBaseUrl;
    } finally {
      inflightLoad = null;
    }
  })();

  return inflightLoad;
}

/**
 * Convenience: load (if not yet loaded) then return the base URL.
 * Prefer this in code paths that are already async.
 */
export async function getBrowserServiceBaseUrl(): Promise<string> {
  return loadBrowserServiceBaseUrl();
}

/**
 * The platform-shared host as the platform names it — the Firestore value,
 * else the compile-time default — whatever the window override says (S1b-3a).
 * Sync: the most recently known value, starting from the compile-time default.
 */
export function getCanonicalSharedBrowserServiceBase(): string {
  return canonicalSharedBase;
}

/** Hydrate getCanonicalSharedBrowserServiceBase from Firestore, once per page (force re-reads). */
export async function loadCanonicalSharedBrowserServiceBase(
  opts: { force?: boolean } = {}
): Promise<string> {
  if (!opts.force) {
    if (canonicalLoaded) return canonicalSharedBase;
    if (inflightCanonicalLoad) return inflightCanonicalLoad;
  }
  inflightCanonicalLoad = (async () => {
    try {
      canonicalSharedBase = await readCanonicalSharedBase();
      canonicalLoaded = true;
    } catch {
      // Keep what we had (the compile-time default at worst); the next caller retries.
    } finally {
      inflightCanonicalLoad = null;
    }
    return canonicalSharedBase;
  })();
  return inflightCanonicalLoad;
}

/**
 * Returns the most recently known browser-service auth token synchronously
 * (2026-08-11), or `null` when none is provisioned / not yet loaded. Mirrors
 * `getCachedBrowserServiceBaseUrl` semantics: window override always wins.
 */
export function getCachedBrowserServiceAuthToken(): string | null {
  const override = readWindowTokenOverride();
  if (override) return override;
  return cachedAuthToken;
}

/**
 * Fetches the platform-shared browser-service auth token from
 * BROWSER_SERVICE_INTERNAL_AUTH_DOC (field `browserServiceToken`) and caches
 * it in module state (2026-08-11; relocated 2026-09-15).
 *
 * Returns `null` when the doc or field is absent, on localhost (dev stays
 * hermetic — a local browser-service has no token), when Firestore DENIES the
 * read (not a verified internal member — by design; held for
 * TOKEN_DENIED_HOLD_MS before one re-ask), or on any other Firestore failure
 * (fail-open on the client: the request goes out without auth and the server
 * decides; the next caller retries). `null` ⇒ callers attach nothing and
 * behave exactly as they did before a token was provisioned.
 */
export async function getBrowserServiceAuthToken(): Promise<string | null> {
  const override = readWindowTokenOverride();
  if (override) return override;
  // Localhost never hits Firestore for this config (matches the base-URL
  // resolver) — local dev talks to an unauthenticated local service.
  if (isLocalhost()) return null;
  if (tokenLoaded) return cachedAuthToken;
  if (tokenDeniedUntilMs > Date.now()) return null;
  if (inflightTokenLoad) return inflightTokenLoad;

  inflightTokenLoad = (async () => {
    try {
      const db = getFirestore(getApp());
      const snap = await getDoc(doc(db, BROWSER_SERVICE_INTERNAL_AUTH_DOC));
      cachedAuthToken = snap.exists()
        ? normalizeTokenField(snap.data()?.[BROWSER_SERVICE_TOKEN_FIELD])
        : null;
      tokenLoaded = true;
      tokenDeniedUntilMs = 0;
      return cachedAuthToken;
    } catch (err) {
      if (isPermissionDenied(err)) {
        // The rules answered: this principal is not a verified member of the
        // internal org, so there is no shared token FOR them. Not an error,
        // not worth a warning, and not worth re-asking on every call.
        cachedAuthToken = null;
        tokenDeniedUntilMs = Date.now() + TOKEN_DENIED_HOLD_MS;
        return null;
      }
      if (!tokenLoadWarned) {
        tokenLoadWarned = true;
        console.warn(
          '[browserService/baseUrl] Failed to load auth token from Firestore — proceeding without one',
          err
        );
      }
      // Do NOT set tokenLoaded=true on error — let the next caller retry.
      return cachedAuthToken;
    } finally {
      inflightTokenLoad = null;
    }
  })();

  return inflightTokenLoad;
}

/**
 * Convenience for fetch call sites (2026-08-11): resolves to
 * `{ Authorization: 'Bearer <token>' }` when a token is provisioned, `{}`
 * otherwise — safe to spread into any headers object unconditionally.
 *
 * 2026-08-19 (P0 hardening): pass the target `base` so the RIGHT token is
 * used per host — the account VM's own token for its `…/browser-api` host,
 * the platform-shared token for the shared service. Without `base` the
 * shared token is used (back-compat). A per-base token is registered by the
 * account-VM resolver (registerBrowserServiceToken); an unregistered base
 * falls through to the shared token, which is exactly today's behavior.
 */
const BASE_TOKEN_OVERRIDES = new Map<string, string>();
/** Per host: the account whose `vm/main` record holds that machine's own key, so a refused
 *  call can re-read it (see "A key that changed under the page" below). */
const BASE_ACCOUNT_IDS = new Map<string, string>();
/** Per host: a key the machine refused and a fresh read then replaced (null = "no key" was what
 *  it refused). A page cache that read it earlier (accountVmBase caches per page load) must not
 *  register it again. */
const REPLACED_BASE_TOKENS = new Map<string, string | null>();

/**
 * Register a per-host browser-service token (account VMs). null clears it.
 * `accountId` names the account whose vm record holds the key, so a call this machine refuses
 * can re-read it; without one, a refusal is simply returned as before.
 */
export function registerBrowserServiceToken(
  base: string,
  token: string | null,
  accountId?: string | null,
): void {
  const key = trimTrailingSlash(base);
  if (!key) return;
  if (accountId && accountId.trim()) BASE_ACCOUNT_IDS.set(key, accountId.trim());
  const next = token && token.trim() ? token.trim() : null;
  if (REPLACED_BASE_TOKENS.has(key) && REPLACED_BASE_TOKENS.get(key) === next) return;
  if (next) BASE_TOKEN_OVERRIDES.set(key, next);
  else BASE_TOKEN_OVERRIDES.delete(key);
}

export async function getBrowserServiceAuthHeaders(
  base?: string
): Promise<Record<string, string>> {
  if (base) {
    const perHost = BASE_TOKEN_OVERRIDES.get(trimTrailingSlash(base));
    if (perHost) return { Authorization: `Bearer ${perHost}` };
    // An account-VM host with no registered token must NOT borrow the
    // platform-shared token (wrong secret → guaranteed 401 once the VM is
    // gated). Only the shared host / localhost uses the shared token.
    if (!isSharedServiceBase(base)) return {};
  }
  const token = await getBrowserServiceAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * True when `base` is the platform-shared service as the PLATFORM names it (or
 * localhost dev): the host the shared internal key and a Prest0 Inc ticket are
 * meant for. Never the window override — see canonicalSharedBase (S1b-3a).
 */
function isCanonicalSharedBase(base: string): boolean {
  const trimmed = trimTrailingSlash(base);
  if (trimmed === LOCALHOST_BROWSER_SERVICE_URL) return true;
  return trimmed === canonicalSharedBase;
}

/**
 * The host the shared key may be sent to: the canonical shared host, or — for
 * a developer who set BOTH window overrides by hand in devtools — the host they
 * named, with the key they typed (getBrowserServiceAuthToken returns the typed
 * key first). Nothing in the app writes the token override, so a link can
 * never make this pairing; `?svc=` can set only the base.
 */
function isSharedServiceBase(base: string): boolean {
  if (isCanonicalSharedBase(base)) return true;
  const override = readWindowOverride();
  if (!override || !readWindowTokenOverride()) return false;
  return trimTrailingSlash(rewriteIfStale(override)) === trimTrailingSlash(base);
}

// ─────────────────────────────────────────────────────────────────────────────
// A key that changed under the page (2026-09-24)
// ─────────────────────────────────────────────────────────────────────────────
//
// Both caches above live for the whole page: the shared key once read, a
// machine's own key once registered. A workstation's key can be installed or
// replaced while a page is open (prest0-vm's was installed at 04:24 UTC on
// 2026-09-24 by install-browser-service-auth-on-existing-vm.sh), and a page
// holding the old key, or none, was refused on every call until the person
// reloaded. So a refused call re-reads the key from where it lives and is sent
// once more, only when the key it re-read differs from the one just refused.
// No refusal: no read. The same key re-read: the refusal is returned as it came.

/** A page that keeps being refused re-reads a host's key at most this often. */
export const KEY_REREAD_MIN_INTERVAL_MS = 15_000;
const KEY_REREAD_AT = new Map<string, number>();
const INFLIGHT_KEY_REREADS = new Map<string, Promise<Record<string, string>>>();

function authHeader(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Which key `base` is called with: its machine's own (keyed by host) or the
 *  shared one (''). The same choice getBrowserServiceAuthHeaders makes, so the
 *  re-read is always of the key that was sent. */
function keyScopeFor(base?: string): { perHost: boolean; key: string } {
  if (base) {
    const key = trimTrailingSlash(base);
    if (BASE_TOKEN_OVERRIDES.has(key) || !isSharedServiceBase(key)) return { perHost: true, key };
  }
  return { perHost: false, key: '' };
}

async function rereadHostKey(key: string): Promise<Record<string, string>> {
  const current = BASE_TOKEN_OVERRIDES.get(key) ?? null;
  const accountId = BASE_ACCOUNT_IDS.get(key);
  if (!accountId) return authHeader(current); // nowhere to re-read it from
  const snap = await getDoc(
    doc(getFirestore(getApp()), ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main')
  );
  const fresh = snap.exists() ? normalizeTokenField(snap.data()?.[BROWSER_SERVICE_TOKEN_FIELD]) : null;
  if (fresh !== current) {
    REPLACED_BASE_TOKENS.set(key, current);
    if (fresh) BASE_TOKEN_OVERRIDES.set(key, fresh);
    else BASE_TOKEN_OVERRIDES.delete(key);
  }
  return authHeader(fresh);
}

async function rereadSharedKey(): Promise<Record<string, string>> {
  tokenLoaded = false;
  return authHeader(await getBrowserServiceAuthToken());
}

/**
 * Re-read the key `base` is called with, past the page cache, and return the
 * headers it gives. Concurrent refusals share one read; a host is re-read at
 * most once per KEY_REREAD_MIN_INTERVAL_MS, and inside that window the answer
 * is the key already held, which the last re-read set.
 */
export async function rereadBrowserServiceAuthHeaders(
  base?: string
): Promise<Record<string, string>> {
  const scope = keyScopeFor(base);
  const inflight = INFLIGHT_KEY_REREADS.get(scope.key);
  if (inflight) return inflight;
  const last = KEY_REREAD_AT.get(scope.key);
  if (last !== undefined && Date.now() - last < KEY_REREAD_MIN_INTERVAL_MS) {
    return getBrowserServiceAuthHeaders(base);
  }
  KEY_REREAD_AT.set(scope.key, Date.now());
  const read = (scope.perHost ? rereadHostKey(scope.key) : rereadSharedKey()).finally(() => {
    INFLIGHT_KEY_REREADS.delete(scope.key);
  });
  INFLIGHT_KEY_REREADS.set(scope.key, read);
  return read;
}

/** Request options whose body can be sent twice (a refused call is resent). */
export type BrowserServiceRequestInit = Omit<RequestInit, 'headers' | 'body'> & {
  headers?: Record<string, string>;
  body?: string;
};

/**
 * Which workspace's workstation `base` is, when a personal ticket can be asked
 * for it (S1b-2b): an account workstation this page registered with its
 * workspace, or the platform-shared host, which is the internal organization's
 * own. Local dev and a host with no known workspace: none.
 */
function ticketAccountFor(base: string | undefined): string | null {
  if (!base || isLocalhost()) return null;
  const key = trimTrailingSlash(base);
  const accountId = BASE_ACCOUNT_IDS.get(key);
  if (accountId) return accountId;
  // The canonical check only: a ticket is a credential for the named machine,
  // and a host a link or devtools named is not one (S1b-3a).
  if (key !== LOCALHOST_BROWSER_SERVICE_URL && isCanonicalSharedBase(key)) return PREST0_INC_ACCOUNT_ID;
  return null;
}

/**
 * fetch() against the browser-service for the signed-in person.
 *
 * A personal ticket goes first where one can be had (workstationTickets.ts,
 * loaded on first use so none of it joins the first download): it opens only
 * this person's own desktops. The ticket module answers null when the call
 * should go with the machine key instead (no ticket to be had, or a machine
 * that does not take tickets yet).
 *
 * With the key: the right key for `base` is attached. When the service refuses
 * the call (401), the key is re-read and the call is sent once more, but only
 * if the re-read key differs from the one refused. Every other answer, and a
 * refusal the re-read cannot change, is returned exactly as it came.
 */
export async function fetchWithBrowserServiceAuth(
  base: string | undefined,
  url: string,
  init: BrowserServiceRequestInit = {}
): Promise<Response> {
  const ticketAccount = ticketAccountFor(base);
  if (ticketAccount) {
    const viaTicket = await import('./workstationTickets')
      .then((m) => m.fetchWithWorkstationTicket(trimTrailingSlash(base as string), ticketAccount, url, init))
      .catch(() => null);
    if (viaTicket) return viaTicket;
  }
  const sent = await getBrowserServiceAuthHeaders(base);
  const res = await fetch(url, { ...init, headers: { ...(init.headers ?? {}), ...sent } });
  if (res.status !== 401) return res;
  const again = await rereadBrowserServiceAuthHeaders(base).catch(() => sent);
  if ((again.Authorization ?? '') === (sent.Authorization ?? '')) return res;
  return fetch(url, { ...init, headers: { ...(init.headers ?? {}), ...again } });
}

/**
 * The window view (PR-9, 2026-09-30): whether this workstation serves a person's
 * view of one window of their browser, and the ticket that view opens (or, with
 * `fresh`, renews) with. Only where a personal ticket can be had (the same rule as
 * above); anywhere else the answer is no, and the window shows the whole screen.
 */
/**
 * Whether a window view could be asked of `base` at all (a machine a personal ticket can be had
 * for), answered at once (PR-9b): a window onto any other host shows the whole screen with no
 * wait, exactly as before the window view existed.
 */
export function windowViewCanBeAskedAt(base: string | undefined): boolean {
  return !!base && !!ticketAccountFor(base);
}

export async function windowViewServedAt(base: string | undefined): Promise<boolean> {
  if (!base || !ticketAccountFor(base)) return false;
  return import('./workstationTickets')
    .then((m) => m.servesWindowViews(trimTrailingSlash(base)))
    .catch(() => false);
}

export async function windowViewTicketAt(base: string | undefined, fresh: boolean): Promise<string | null> {
  const account = ticketAccountFor(base);
  if (!base || !account) return null;
  return import('./workstationTickets')
    .then((m) => m.ticketForWindowView(trimTrailingSlash(base), account, fresh))
    .catch(() => null);
}

/**
 * The key a machine enforces, read past every page cache: the shared internal
 * key, or an account's own `vm/main` key. For a page that hands the key on
 * (the /desktop shell answering its embedded page), so it never hands on a
 * value from before the key was installed or replaced. null when there is
 * none, the read is refused, or it fails.
 */
export async function readBrowserServiceKeyFresh(
  target: { kind: 'internal' } | { kind: 'account'; accountId: string }
): Promise<string | null> {
  try {
    if (target.kind === 'internal') {
      tokenLoaded = false;
      return await getBrowserServiceAuthToken();
    }
    if (!target.accountId) return null;
    const snap = await getDoc(
      doc(getFirestore(getApp()), ACCOUNT_FIRESTORE_PREFIX, target.accountId, 'vm', 'main')
    );
    return snap.exists() ? normalizeTokenField(snap.data()?.[BROWSER_SERVICE_TOKEN_FIELD]) : null;
  } catch {
    return null;
  }
}

/**
 * WebSocket equivalent of the browser-service base URL.
 * `https://host/browser` → `wss://host/browser`
 */
export function browserServiceWsFromBase(base: string): string {
  const trimmed = trimTrailingSlash(base);
  if (trimmed.startsWith('https://')) return 'wss://' + trimmed.slice('https://'.length);
  if (trimmed.startsWith('http://')) return 'ws://' + trimmed.slice('http://'.length);
  return trimmed;
}

// desktopWsFromBase was deleted 2026-08-19: it targeted /desktop/websockify,
// a path no Caddy config ever served — dead code from the removed
// /agent-activity React tree. The interactive channel is the VM-served noVNC
// page (buildDesktopNoVncUrl in services/composio/desktopSignIn.ts).

// ─────────────────────────────────────────────────────────────────────────────
// Module-load hydration (best-effort, fire-and-forget)
// ─────────────────────────────────────────────────────────────────────────────

// Kick off a background load on import so callers that use the sync getter
// later in the same page-load tend to see the Firestore-resolved value.
// Skipped on SSR / when no `window` is available.
if (typeof window !== 'undefined') {
  // Defer to next tick so consumers can still set window override before we
  // race ahead with Firestore (in tests, etc.).
  Promise.resolve().then(() => {
    void loadBrowserServiceBaseUrl().catch(() => {
      /* swallow — already logged */
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Test hooks
// ─────────────────────────────────────────────────────────────────────────────

/** @internal — for unit tests only. */
export function __resetBrowserServiceBaseUrlCacheForTests(
  next?: string
): void {
  cachedBaseUrl = trimTrailingSlash(next ?? compileTimeDefault());
  loaded = false;
  inflightLoad = null;
  // 2026-09-26 (S1b-3a): the shared host a test names is the canonical one.
  canonicalSharedBase = cachedBaseUrl;
  canonicalLoaded = false;
  inflightCanonicalLoad = null;
  // 2026-08-11: auth-token cache resets alongside the URL cache.
  cachedAuthToken = null;
  tokenLoaded = false;
  inflightTokenLoad = null;
  tokenLoadWarned = false;
  tokenDeniedUntilMs = 0;
  // 2026-09-24: the per-host keys and the re-read bookkeeping reset with them.
  BASE_TOKEN_OVERRIDES.clear();
  BASE_ACCOUNT_IDS.clear();
  REPLACED_BASE_TOKENS.clear();
  KEY_REREAD_AT.clear();
  INFLIGHT_KEY_REREADS.clear();
}
